import type { ActivitySessionDetail, AwardBadgeResult, SessionUpdateInput } from 'shared';
import {
  combineDateTime,
  lastDay,
  toDateInput,
  toTimeInput,
  windowError,
} from '@/lib/activityTime';

// The session details form: its shape, how it is read from a session and how
// it becomes an API body. Kept apart from the component so it can be tested
// without rendering anything.

export const MAX_FIELD = 255;
export const MAX_NOTES = 5000;
export const MAX_HEADCOUNT = 10000;

export interface DetailsForm {
  date: string;
  /** The last day of an open day that lasts several; empty when it ends the day it starts. */
  endDate: string;
  start: string;
  end: string;
  leaderUserId: number;
  visitorOrganization: string;
  visitorContact: string;
  visitorHeadcount: string;
  notes: string;
}

export type FormErrors = Partial<Record<keyof DetailsForm, string>>;

// The API reports problems against its own field names.
export const SERVER_FIELD: Record<string, keyof DetailsForm> = {
  startsAt: 'start',
  endsAt: 'end',
  leaderUserId: 'leaderUserId',
  visitorOrganization: 'visitorOrganization',
  visitorContact: 'visitorContact',
  visitorHeadcount: 'visitorHeadcount',
  notes: 'notes',
};

export function toForm(session: ActivitySessionDetail): DetailsForm {
  const start = new Date(session.startsAt);
  const end = new Date(session.endsAt);
  const firstDay = toDateInput(start);
  const finalDay = toDateInput(end);
  return {
    date: firstDay,
    endDate: finalDay === firstDay ? '' : finalDay,
    start: toTimeInput(start),
    end: toTimeInput(end),
    leaderUserId: session.leaderUserId,
    visitorOrganization: session.visitorOrganization ?? '',
    visitorContact: session.visitorContact ?? '',
    visitorHeadcount: session.visitorHeadcount != null ? String(session.visitorHeadcount) : '',
    notes: session.notes ?? '',
  };
}

/**
 * Validates the form and, when it is sound, builds the API body from it.
 * `singleEvent` says the session is an open day, which may last several days.
 */
export function buildSessionUpdate(
  form: DetailsForm,
  singleEvent = false,
): { errors: FormErrors; input: SessionUpdateInput | null } {
  const errors: FormErrors = {};

  const startsAt = combineDateTime(form.date, form.start);
  const endsAt = combineDateTime(lastDay(form), form.end);
  if (startsAt === null) errors.date = 'Data ou hora de início inválida.';
  if (endsAt === null) errors.end = 'Hora de fim inválida.';
  else if (startsAt !== null) {
    const problem = windowError(startsAt, endsAt, singleEvent);
    if (problem) errors.end = problem;
  }

  const headcountText = form.visitorHeadcount.trim();
  const headcount = headcountText === '' ? null : Number(headcountText);
  if (
    headcount !== null &&
    (!Number.isInteger(headcount) || headcount < 0 || headcount > MAX_HEADCOUNT)
  ) {
    errors.visitorHeadcount = `Indica um número inteiro entre 0 e ${MAX_HEADCOUNT}.`;
  }

  if (form.visitorOrganization.trim().length > MAX_FIELD) {
    errors.visitorOrganization = `Máximo de ${MAX_FIELD} caracteres.`;
  }
  if (form.visitorContact.trim().length > MAX_FIELD) {
    errors.visitorContact = `Máximo de ${MAX_FIELD} caracteres.`;
  }
  if (form.notes.trim().length > MAX_NOTES) {
    errors.notes = `Máximo de ${MAX_NOTES} caracteres.`;
  }

  if (Object.keys(errors).length > 0 || startsAt === null || endsAt === null) {
    return { errors, input: null };
  }
  return {
    errors,
    input: {
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      leaderUserId: form.leaderUserId,
      visitorOrganization: form.visitorOrganization.trim() || null,
      visitorContact: form.visitorContact.trim() || null,
      visitorHeadcount: headcount,
      notes: form.notes.trim() || null,
    },
  };
}

/** "1 participante" / "3 participantes" */
export function participants(n: number): string {
  return `${n} participante${n !== 1 ? 's' : ''}`;
}

/** What the toast says once the badge has been handed out. */
export function awardSummary({ awarded, alreadyHeld }: AwardBadgeResult): string {
  const given =
    awarded === 0
      ? 'Ninguém recebeu a credencial.'
      : `Credencial atribuída a ${participants(awarded)}.`;
  if (alreadyHeld === 0) return given;
  const held = alreadyHeld === 1 ? '1 participante já a tinha.' : `${alreadyHeld} participantes já a tinham.`;
  return `${given} ${held}`;
}
